import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios';
import { useAuthStore } from '../application/store/AuthStore';
import { APP_CONFIG } from '../config/app.config';
import { getImpersonationTargetUserId, IMPERSONATE_USER_HEADER } from '../application/auth/impersonation';

declare module 'axios' {
    export interface InternalAxiosRequestConfig {
        retry?: number;
        retryDelay?: number;
    }
}

export class ApiClient {
    private axiosInstance: AxiosInstance;

    constructor(baseURL: string) {
        this.axiosInstance = axios.create({
            baseURL,
            timeout: APP_CONFIG.api.timeout,
            withCredentials: true,
        });

        this.setupInterceptors();
    }

    private setupInterceptors() {
        this.axiosInstance.interceptors.request.use(async (config) => {
            try {
                const token = await useAuthStore.getState().getJwt();
                const companyId = useAuthStore.getState().companyId;

                if (token) {
                    config.headers.Authorization = `Bearer ${token}`;
                }
                if (companyId) {
                    config.headers['X-Company-ID'] = companyId;
                }

                const impersonationTargetUserId = getImpersonationTargetUserId();
                if (impersonationTargetUserId) {
                    config.headers[IMPERSONATE_USER_HEADER] = impersonationTargetUserId;
                } else {
                    delete config.headers[IMPERSONATE_USER_HEADER];
                }
            } catch (error) {
                console.error('Failed to get auth credentials:', error);
            }

            // Add retry logic based on config
            config.retry = APP_CONFIG.errors.retry.maxAttempts;
            config.retryDelay = APP_CONFIG.errors.retry.baseDelay;

            return config;
        });
    }

    async get<T>(url: string, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> {
        const response = await this.axiosInstance.get<T>(url, config);
        return response;
    }

    async post<T>(url: string, data?: any, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> {
        const response = await this.axiosInstance.post<T>(url, data, config);
        return response;
    }

    async put<T>(url: string, data?: any, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> {
        const response = await this.axiosInstance.put<T>(url, data, config);
        return response;
    }

    async patch<T>(url: string, data?: any, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> {
        const response = await this.axiosInstance.patch<T>(url, data, config);
        return response;
    }

    async delete<T>(url: string, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> {
        const response = await this.axiosInstance.delete<T>(url, config);
        return response;
    }
} 